import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { 
  Shield, Users, BarChart3, Settings, Trophy, CreditCard, 
  DollarSign, ShoppingBag, Wallet, FileText, 
  Activity, Users as UsersIcon, LogOut, ChevronDown, ChevronRight, Package, AlertTriangle, Mail, Globe, Ticket, UserCog
} from 'lucide-react';

const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [openMenus, setOpenMenus] = useState({});

  // Menu structure with categories and submenus
  const menuStructure = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: BarChart3,
      single: true
    },
    {
      name: 'Users & Admins',
      icon: Users,
      items: [
        { name: 'Users', href: '/admin/users', icon: Users },
        { name: 'Admins', href: '/admin/admins', icon: Shield },
      ]
    },
    {
      name: 'Game Management',
      icon: Trophy,
      items: [
        { name: 'GameWeeks', href: '/admin/gameweeks', icon: Trophy },
        { name: 'Scores', href: '/admin/scores', icon: Activity },
        { name: 'Classifiche Mensili', href: '/admin/monthly-rankings', icon: BarChart3 },
        { name: 'Cards', href: '/admin/cards', icon: CreditCard },
        { name: 'Card Allenatore', href: '/admin/coach-cards', icon: UserCog },
        { name: 'Packs', href: '/admin/packs', icon: Package },
        { name: 'Mondiali', href: '/admin/mondiali', icon: Globe },
        { name: 'Mercato secondario', href: '/admin/secondary-market', icon: ShoppingBag },
      ]
    },
    {
      name: 'Economy & Finance',
      icon: DollarSign,
      items: [
        { name: 'Economy', href: '/admin/economy', icon: DollarSign },
        { name: 'Marketplace', href: '/admin/marketplace', icon: ShoppingBag },
        { name: 'Withdrawals', href: '/admin/withdrawals', icon: Wallet },
        { name: 'Promo Code', href: '/admin/promo-code', icon: Ticket },
      ]
    },
    {
      name: 'System',
      icon: Settings,
      items: [
        { name: 'Affiliations', href: '/admin/affiliations', icon: UsersIcon },
        { name: 'Security Reports', href: '/admin/security-reports', icon: AlertTriangle },
        { name: 'Communications', href: '/admin/communications', icon: FileText },
        { name: 'Email piattaforma', href: '/admin/platform-email', icon: Mail },
        { name: 'Logs', href: '/admin/logs', icon: FileText },
        { name: 'PayPal Deposits', href: '/admin/paypal-deposits', icon: CreditCard },
        { name: 'Settings', href: '/admin/settings', icon: Settings },
        { name: 'Player', href: '/admin/settings/players', icon: Users },
      ]
    },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  useEffect(() => {
    // Check if user is logged in and is admin
    const user = authApi.getCurrentUser();
    const token = localStorage.getItem('authToken');
    
    if (user && token && user.isAdmin) {
      setIsAuthenticated(true);
    } else {
      // If not admin or not logged in, redirect to home
      if (location.pathname !== '/admin') {
        navigate('/');
      } else {
        navigate('/');
      }
    }
    setChecking(false);
  }, [location.pathname, navigate]);

  // Auto-open menu if current path matches any item in it
  useEffect(() => {
    const newOpenMenus = {};
    menuStructure.forEach(menu => {
      if (menu.items) {
        const hasActiveItem = menu.items.some(item => isActive(item.href));
        if (hasActiveItem) {
          newOpenMenus[menu.name] = true;
        }
      }
    });
    setOpenMenus(prev => ({ ...prev, ...newOpenMenus }));
  }, [location.pathname]);

  // Show loading while checking auth
  if (checking) {
    return (
      <div className="admin-container">
        <div className="admin-background" style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}></div>
        <div className="min-h-screen flex items-center justify-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2"></div>
        </div>
      </div>
    );
  }

  // If not authenticated, don't render (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  // Helper function for logout
  const handleLogout = () => {
    authApi.logout();
    navigate('/');
  };

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  return (
    <div className="admin-container">
      {/* Admin Background */}
      <div 
        className="admin-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      {/* Admin Header */}
      <nav className="admin-navbar sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-start admin-navbar-content">
            <div className="flex items-center space-x-8">
              <Link to="/admin" className="flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-xl font-bold">D10S Admin</span>
              </Link>
              <div className="hidden lg:flex items-center space-x-1">
                {menuStructure.map((menu) => {
                  if (menu.single) {
                    const Icon = menu.icon;
                    return (
                      <Link
                        key={menu.name}
                        to={menu.href}
                        onClick={() => setOpenMenus({})}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                          isActive(menu.href)
                            ? 'text-blue-400 bg-blue-400/10'
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{menu.name}</span>
                      </Link>
                    );
                  }
                  
                  // Menu with submenu
                  const Icon = menu.icon;
                  const isOpen = openMenus[menu.name];
                  const hasActiveItem = menu.items.some(item => isActive(item.href));
                  
                  return (
                    <div key={menu.name} className="admin-menu-item">
                      <button
                        onClick={() => toggleMenu(menu.name)}
                        className={`admin-menu-button flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                          hasActiveItem
                            ? 'text-blue-400 bg-blue-400/10'
                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{menu.name}</span>
                        {isOpen ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      
                      {isOpen && (
                        <div className="admin-dropdown-menu">
                          {menu.items.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setOpenMenus({})}
                                className={`flex items-center space-x-2 px-4 py-2 transition-colors ${
                                  isActive(item.href)
                                    ? 'bg-blue-400/10'
                                    : ''
                                }`}
                              >
                                <ItemIcon className="w-4 h-4" />
                                <span className="text-sm">{item.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-sm flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
              <Link
                to="/"
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-sm"
              >
                Back to App
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className="admin-mobile-menu lg:hidden">
        <div className="px-4 py-2 space-y-1">
          {menuStructure.map((menu) => {
            if (menu.single) {
              const Icon = menu.icon;
              return (
                <Link
                  key={menu.name}
                  to={menu.href}
                  onClick={() => setOpenMenus({})}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors admin-mobile-link ${
                    isActive(menu.href)
                      ? 'text-blue-400 bg-blue-400/10'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{menu.name}</span>
                </Link>
              );
            }
            
            const Icon = menu.icon;
            const isOpen = openMenus[menu.name];
            const hasActiveItem = menu.items.some(item => isActive(item.href));
            
            return (
              <div key={menu.name}>
                <button
                  onClick={() => toggleMenu(menu.name)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    hasActiveItem
                      ? 'text-blue-400 bg-blue-400/10'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{menu.name}</span>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {isOpen && (
                  <div className="admin-mobile-submenu ml-4 mt-1 space-y-1">
                    {menu.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setOpenMenus({})}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors admin-mobile-sublink ${
                            isActive(item.href)
                              ? 'text-blue-400 bg-blue-400/10'
                              : 'text-gray-300 hover:text-white hover:bg-gray-700'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4" />
                          <span className="text-sm">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Admin Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
